'use client';
import { upsellViewedEvent } from '#pplx/frontend/apps/ask/events/unified-upsell/analytics.js';
import { isCtaSupported } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/executeCtaHandler.js';
import { useHomepageUpsellsQuery } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/useHomepageUpsellsQuery.js';
import { useHomepageTopBannerUpsellAction } from '#pplx/frontend/apps/ask/upsell-slots/homepage-top-banner/internal/useHomepageTopBannerUpsellAction.js';
import { buildUpsellSlotAnalytics } from '#pplx/frontend/apps/ask/upsell-slots/UpsellSlotRenderPlan.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect, useState } from 'react';
const resolveHomepageTopBannerUpsellSlotPlan = ({ input, data, controls, }) => {
    if (input.callsite.enabled === false) {
        return { status: 'empty', show: false, reason: 'disabled' };
    }
    if (input.callsite.surface?.hasPrimarySurfaceContent) {
        return { status: 'blocked', show: false, reason: 'competing_surface' };
    }
    const { productBanner } = data;
    if (!productBanner) {
        return { status: 'empty', show: false, reason: 'no_backend_upsell' };
    }
    if (!productBanner.app_location ||
        !input.acceptedLocations.includes(productBanner.app_location)) {
        return { status: 'blocked', show: false, reason: 'location_mismatch' };
    }
    if (!productBanner.description || !productBanner.button_text) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    if (productBanner.cta && !isCtaSupported(productBanner.cta)) {
        return { status: 'blocked', show: false, reason: 'unsupported_cta' };
    }
    if (data.sessionDismissed) {
        return { status: 'blocked', show: false, reason: 'dismissed' };
    }
    return {
        status: 'ready',
        show: true,
        slotId: input.slotId,
        upsellInformation: productBanner,
        render: {
            kind: 'home_top_banner',
            componentKey: 'HomepageTopBanner',
            props: {
                description: productBanner.description,
                primaryText: productBanner.button_text,
                secondaryText: productBanner.cta_information?.secondary_button?.button_text,
                shouldShow: true,
                onDismiss: controls.onDismiss,
                onPrimary: controls.onPrimary,
            },
        },
        actions: controls,
        analytics: {
            ...buildUpsellSlotAnalytics(input.slotId, productBanner),
            cta: productBanner.cta,
        },
    };
};
export const useHomepageTopBannerUpsellSlotPlan = (input) => {
    const enabled = input.callsite.enabled !== false && !input.callsite.surface?.hasPrimarySurfaceContent;
    const { productBanner } = useHomepageUpsellsQuery({ enabled });
    const { impression } = useInstrumentation();
    const [sessionDismissed, setSessionDismissed] = useState(false);
    const hideUpsell = useCallback(() => setSessionDismissed(true), []);
    const onDismiss = useHomepageTopBannerUpsellAction({
        upsellInformation: productBanner || undefined,
        options: { hideUpsell, interactionType: 'dismiss' },
    });
    const onPrimary = useHomepageTopBannerUpsellAction({
        upsellInformation: productBanner || undefined,
    });
    const controls = {
        onDismiss,
        onPrimary,
    };
    const plan = resolveHomepageTopBannerUpsellSlotPlan({
        input,
        data: {
            productBanner,
            sessionDismissed,
        },
        controls,
    });
    const readyUpsellInformation = plan.show && plan.upsellInformation.app_location ? plan.upsellInformation : null;
    useEffect(() => {
        if (!readyUpsellInformation) {
            return;
        }
        impression(upsellViewedEvent({
            name: 'upsell viewed',
            data: {
                upsellLocation: readyUpsellInformation.app_location ?? '',
                upsellUUID: readyUpsellInformation.upsell_uuid ?? '',
                upsellName: readyUpsellInformation.name ?? '',
                upsellType: readyUpsellInformation.upsell_type ?? '',
                cta: readyUpsellInformation.cta,
            },
        }));
    }, [
        readyUpsellInformation,
        readyUpsellInformation?.app_location,
        readyUpsellInformation?.cta,
        readyUpsellInformation?.name,
        readyUpsellInformation?.upsell_type,
        readyUpsellInformation?.upsell_uuid,
        impression,
    ]);
    return plan;
};
//# sourceMappingURL=useHomepageTopBannerUpsellSlotPlan.js.map